/**
 * Funções puras reaproveitáveis da importação de notas (sem dependência de Vue).
 * Extraídas do legado (TelaDeImportacaoDeNota.vue) para serem testáveis e
 * compartilhadas pela tela nova.
 */
import type { ImportacaoDeNota, ProdutoDaImportacao } from '@/models/ImportacaoDeNota'
import cartesianProduct from 'cartesian-product'

/** Extrai a chave de acesso (chNFe) do XML/string do item de processamento. */
export function obterChaveNota(xmlString: string): string {
	if (!xmlString) return ''
	const match = xmlString.match(/<chNFe>(\d+)<\/chNFe>/)
	return match ? match[1] : ''
}

/** Extrai o número da nota (nNF) do XML/string do item de processamento. */
export function obterNumeroNota(xmlString: string): string | null {
	if (!xmlString) return null
	const match = xmlString.match(/<nNF>(\d+)<\/nNF>/)
	return match ? 'Nº ' + match[1] : null
}

export type StatusProcessamento =
	| 'Pendente'
	| 'Em Andamento'
	| 'Concluído'
	| 'Incompleto'
	| string

export function corDoStatus(status: StatusProcessamento): string {
	const cores: Record<string, string> = {
		Pendente: 'orange darken-2',
		'Em Andamento': 'blue',
		'Concluído': 'success',
		Incompleto: 'error',
	}
	return cores[status] || 'grey'
}

export function iconeDoStatus(status: StatusProcessamento): string {
	const icones: Record<string, string> = {
		Pendente: 'mdi-clock-outline',
		'Em Andamento': 'mdi-loading mdi-spin',
		'Concluído': 'mdi-check-circle',
		Incompleto: 'mdi-alert-circle',
	}
	return icones[status] || 'mdi-help-circle'
}

/** True quando o processamento ainda está em curso (vale continuar o polling). */
export function estaEmProcessamento(status: StatusProcessamento): boolean {
	return status === 'Pendente' || status === 'Em Andamento'
}

// --- Triagem de itens (conferência) ---

export type StatusDoItem = 'vinculado' | 'pendente'

/** Um item está "vinculado" quando já aponta para um produto do catálogo (existente ou criado na importação). */
export function statusDoItem(p: ProdutoDaImportacao): StatusDoItem {
	const vinculado = !!(p.encontrado && (p.encontrado.id || p.isProdutoComReferenciaCriadaNaImportacao))
	return vinculado ? 'vinculado' : 'pendente'
}

export interface ResumoDaTriagem {
	total: number
	vinculados: number
	pendentes: number
}

/** Conta itens por status, ignorando os que foram agrupados dentro de outro item. */
export function resumoDaTriagem(produtos: ProdutoDaImportacao[]): ResumoDaTriagem {
	const ativos = (produtos || []).filter(p => !p.isProdutoEstaAgrupado)
	const vinculados = ativos.filter(p => statusDoItem(p) === 'vinculado').length
	return { total: ativos.length, vinculados, pendentes: ativos.length - vinculados }
}

// --- Agrupamento de nota aberta (variantes da mesma referência) ---

/** Nome da referência sem o sufixo de variante ("BLUSA ... Cor: X Tam: Y" → "BLUSA ..."). */
export function nomeDaReferencia(xProd: string): string {
	return String(xProd || '').replace(/\s*(Cor:|Tam:|Var:).*$/i, '').trim() || String(xProd || '')
}

/**
 * Agrupa linhas da MESMA referência (mesmo cProd, cEAN distintos = variantes de nota aberta)
 * num único item: o primeiro vira a referência (qCom = soma, nome sem variante), os demais
 * ficam com isProdutoEstaAgrupado=true (escondidos e EXCLUÍDOS do finalizar). Idempotente.
 */
export function agruparNotaAberta(produtos: ProdutoDaImportacao[]): number {
	if (!produtos || produtos.some(p => p.isProdutoEstaAgrupado || p.contemAgrupamentoDeItens)) return 0
	const porRef: Record<string, ProdutoDaImportacao[]> = {}
	produtos.forEach(p => {
		if (!p.nota || !p.nota.cProd) return
		;(porRef[p.nota.cProd] = porRef[p.nota.cProd] || []).push(p)
	})
	let referenciasAgrupadas = 0
	Object.values(porRef).forEach(linhas => {
		if (linhas.length < 2) return
		const eansDistintos = new Set(linhas.map(l => l.nota.cEAN)).size
		if (eansDistintos < 2) return // linhas realmente iguais — não é nota aberta
		const base = linhas[0]
		const somaQ = linhas.reduce((s, l) => s + (Number(l.nota.qCom) || 0), 0)
		base.produtosAgrupados = linhas.map(l => ({ ...l, nota: { ...l.nota } })) as any
		base.contemAgrupamentoDeItens = true
		base.nota = { ...base.nota, xProd: nomeDaReferencia(base.nota.xProd), qCom: String(somaQ) }
		linhas.slice(1).forEach(l => { l.isProdutoEstaAgrupado = true })
		referenciasAgrupadas++
	})
	return referenciasAgrupadas
}

/** Descrição normalizada (sem Cor/Tam/Var, maiúscula) para comparar similaridade. */
export function descricaoNormalizada(xProd: string): string {
	return nomeDaReferencia(xProd).toUpperCase().replace(/\s+/g, ' ').trim()
}

/** Similaridade entre duas descrições (0 a 1): igualdade da base ou sobreposição de palavras (Jaccard). */
export function similaridadeDescricao(a: string, b: string): number {
	const na = descricaoNormalizada(a)
	const nb = descricaoNormalizada(b)
	if (!na || !nb) return 0
	if (na === nb) return 1
	const ta = new Set(na.split(' ').filter(Boolean))
	const tb = new Set(nb.split(' ').filter(Boolean))
	const inter = [...ta].filter(t => tb.has(t)).length
	const uni = new Set([...ta, ...tb]).size
	return uni ? inter / uni : 0
}

/**
 * Agrupa MANUALMENTE itens selecionados numa referência (independe de cProd igual
 * ou de descrição padronizada). O primeiro vira a referência; os demais ficam ocultos
 * e excluídos do finalizar. Retorna true se agrupou.
 */
export function agruparItensManualmente(selecionados: ProdutoDaImportacao[]): boolean {
	const linhas = (selecionados || []).filter(p => !p.isProdutoEstaAgrupado)
	if (linhas.length < 2) return false
	const base = linhas[0]
	const somaQ = linhas.reduce((s, l) => s + (Number(l.nota.qCom) || 0), 0)
	base.produtosAgrupados = linhas.map(l => ({ ...l, nota: { ...l.nota } })) as any
	base.contemAgrupamentoDeItens = true
	base.nota = { ...base.nota, xProd: nomeDaReferencia(base.nota.xProd), qCom: String(somaQ) }
	linhas.slice(1).forEach(l => { l.isProdutoEstaAgrupado = true })
	return true
}

/** Desfaz o agrupamento de uma referência, reativando as linhas e limpando o vínculo. */
export function desagruparItem(produtos: ProdutoDaImportacao[], base: ProdutoDaImportacao): void {
	const agrupados: any[] = (base.produtosAgrupados as any) || []
	if (agrupados[0]) base.nota = { ...agrupados[0].nota } // restaura a nota original da base
	agrupados.slice(1).forEach(copia => {
		const linha = (produtos || []).find(p =>
			p.isProdutoEstaAgrupado && p.nota && copia.nota &&
			p.nota.cProd === copia.nota.cProd && p.nota.cEAN === copia.nota.cEAN,
		)
		if (linha) linha.isProdutoEstaAgrupado = false
	})
	base.contemAgrupamentoDeItens = false
	base.produtosAgrupados = [] as any
	base.encontrado = null
}

// --- Validações inline (conferência) ---

export const LIMITE_NOME = 60

/** Nome do produto (xProd) acima do limite aceito pelo cadastro. */
export function nomeMuitoLongo(p: ProdutoDaImportacao): boolean {
	return !!(p.nota && p.nota.xProd && p.nota.xProd.length > LIMITE_NOME)
}

/**
 * Duplicata REAL = mesmo cProd E mesmo cEAN (linha repetida de fato).
 * cProd repetido com cEAN diferente é variante de uma mesma referência
 * (nota aberta) — comportamento normal, não deve ser sinalizado como erro.
 */
export function cProdDuplicado(p: ProdutoDaImportacao, produtos: ProdutoDaImportacao[]): boolean {
	if (!p.nota || !p.nota.cProd) return false
	const { cProd, cEAN } = p.nota
	return (produtos || []).filter(x =>
		!x.isProdutoEstaAgrupado && x.nota &&
		x.nota.cProd === cProd && x.nota.cEAN === cEAN,
	).length > 1
}

/** Lista de SKUs (de produtos criados/encontrados) que se repetem na importação. */
export function skusDuplicados(produtos: ProdutoDaImportacao[]): string[] {
	const contagem: Record<string, number> = {}
	;(produtos || []).forEach(p => {
		const sku = p.encontrado && (p.encontrado as any).sku
		if (sku) contagem[sku] = (contagem[sku] || 0) + 1
	})
	return Object.keys(contagem).filter(sku => contagem[sku] > 1)
}

/** Problemas do item (para exibir warnings na conferência). */
export function problemasDoItem(p: ProdutoDaImportacao, produtos: ProdutoDaImportacao[]): string[] {
	const probs: string[] = []
	if (nomeMuitoLongo(p)) probs.push(`Nome acima de ${LIMITE_NOME} caracteres`)
	if (cProdDuplicado(p, produtos)) probs.push('Código (cProd) duplicado na nota')
	return probs
}

/** Markup configurado no fornecedor encontrado (para sugerir a margem geral). */
export function markupDoFornecedor(importacao: any): number {
	const f = importacao && importacao.fornecedor && importacao.fornecedor.encontrado
	return f && f.markup ? Number(f.markup) || 0 : 0
}

// --- Distribuição de quantidade entre variantes (nota fechada na referência) ---

/** Lê um atributo (Tamanho/Cor) de uma variante a partir de atributosVariante. */
export function atributoDaVariante(variante: any, nome: string): string {
	const attrs = variante && variante.atributosVariante ? variante.atributosVariante : []
	const achado = attrs.find((a: any) => String(a.nome || '').toUpperCase() === nome.toUpperCase())
	return achado ? achado.valor : '—'
}

/** Item vinculado a um produto "Com Variantes" (e não agrupado em outro). */
export function ehComVariantes(p: ProdutoDaImportacao): boolean {
	return !!(p.encontrado && (p.encontrado as any).tipo === 'Com Variantes' && !p.isProdutoEstaAgrupado)
}

function variantesDe(p: ProdutoDaImportacao): any[] {
	return (p.encontrado && (p.encontrado as any).variantes) || []
}

/** Soma das quantidades distribuídas nas variantes. */
export function somaDistribuida(p: ProdutoDaImportacao): number {
	return variantesDe(p).reduce((s, v) => s + (Number(v.quantidadeImportacao) || 0), 0)
}

/** Saldo a distribuir = qCom da nota − soma já distribuída. */
export function saldoDoItem(p: ProdutoDaImportacao): number {
	return (Number(p.nota.qCom) || 0) - somaDistribuida(p)
}

/** Todos os itens "Com Variantes" estão com a soma == qCom. */
export function distribuicaoCompleta(produtos: ProdutoDaImportacao[]): boolean {
	return (produtos || []).filter(ehComVariantes).every(p => saldoDoItem(p) === 0)
}

/** Espalha o qCom igualmente entre as variantes (resto vai pras primeiras). */
export function distribuirIgualmente(p: ProdutoDaImportacao): void {
	const vs = variantesDe(p)
	if (!vs.length) return
	const total = Math.max(0, Math.trunc(Number(p.nota.qCom) || 0))
	const base = Math.floor(total / vs.length)
	let resto = total - base * vs.length
	vs.forEach(v => { v.quantidadeImportacao = base + (resto-- > 0 ? 1 : 0) })
}

// --- Criação de produto (grade/variantes) ---

const semEspacos = (s: string) => String(s || '').replace(/\s+/g, '')

/** Gera um EAN-13 com dígito verificador válido a partir de uma base numérica. */
export function gerarEanValido(base: number): string {
	const doze = String(base).slice(-12).padStart(12, '0')
	const dig = doze.split('').map(Number)
	const soma = dig.reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 1 : 3), 0)
	const dv = (10 - (soma % 10)) % 10
	return doze + String(dv)
}

/**
 * Gera as variantes (produto cartesiano Cor × Tamanho) para um produto criado na importação.
 * `atributosProduto`: lista no formato { atributo: { nome }, valores: string[], id }.
 * Extraído do legado (criarVariantesDoProduto).
 */
export function criarVariantesDoProduto(produto: any, atributosProduto: any[], seed = 1): any[] {
	const atributosDaVariante = atributosProduto
		.filter(a => a.valores && a.valores.length)
		.map(a => a.valores.map((valor: string) => ({ id: a.id || '', nome: a.atributo.nome, valor })))

	const variacoes: any[] = cartesianProduct(atributosDaVariante)

	return variacoes.map((variacao: any, index: number) => {
		const valorDe = (nome: string) => {
			const v = variacao.find((x: any) => String(x.nome).toUpperCase() === nome)
			return v ? v.valor : ''
		}
		const cor = valorDe('COR')
		const tam = valorDe('TAMANHO')
		return {
			id: '', ncm: produto.ncm || null, cest: null, regraFiscal: null,
			idProdutoPai: produto.id, tipo: 'Variante', tags: produto.tags || [],
			descricao: null, categoria: produto.categoria || null, urlImagens: produto.urlImagens || [],
			sku: (produto.sku || '') + semEspacos(tam.toUpperCase()) + semEspacos(cor.toUpperCase()),
			preco: null, pesoLiquido: produto.pesoLiquido, pesoBruto: produto.pesoBruto,
			descontoVarejo: null, marca: produto.marca || null, nome: produto.nome,
			atributosVariante: variacao, temGarantia: produto.temGarantia, regrasFiscais: [],
			eans: [gerarEanValido(seed + index)],
			unidadeDeMedida: produto.unidadeDeMedida, identificadores: [],
			produtoDesativado: produto.produtoDesativado, produtoIntegrado: produto.produtoIntegrado,
			genero: produto.genero || null, fatorDeConversao: produto.fatorDeConversao,
			unidadeDeConversao: produto.unidadeDeConversao, estacao: produto.estacao || null,
			isValePresente: false, skuPai: produto.skuPai || produto.sku, quantidadeImportacao: 0,
			fornecedor: produto.fornecedor || null, precoCompra: null, margemLucro: null,
			nomeCompleto: `${produto.nome} ${cor}/${tam}`,
			varianteVinculada: [], vinculada: false, produtoIncompleto: false,
			familiasDoProduto: produto.familiasDoProduto,
			tipoDaOrigemDoProduto: produto.tipoDaOrigemDoProduto,
			codigoOrigemDoProduto: produto.codigoOrigemDoProduto,
		}
	})
}

// --- Precificação (margem de lucro / preço de venda) ---

export interface OpcoesArredondamento {
	/** Quando true, ignora o arredondamento decimal (mantém as 2 casas). */
	naoUsar?: boolean
	/** Valor de centavos quando o decimal cai abaixo de 0,50 (ex.: 0,00 → 15,23 vira 15,00). */
	abaixo?: number
	/** Valor de centavos quando o decimal é ≥ 0,50 (ex.: 0,99 → 15,67 vira 15,99). */
	acima?: number
}

/** Arredonda o preço para o centavo configurado (igual ao legado: floor + abaixo/acima). */
export function arredondarPreco(preco: number, opcoes?: OpcoesArredondamento): number {
	if (!opcoes || opcoes.naoUsar) return preco
	const inteiro = Math.floor(preco)
	const decimal = preco - inteiro
	const abaixo = Number(opcoes.abaixo) || 0
	const acima = opcoes.acima == null ? 0.99 : Number(opcoes.acima)
	return inteiro + (decimal >= 0.5 ? acima : abaixo)
}

/** Preço de venda = preço de compra (vUnCom) × (1 + margem%), arredondado a 2 casas e ao centavo configurado. */
export function precoComMargem(vUnCom: string | number, margem: number, arred?: OpcoesArredondamento): number {
	const compra = Number(String(vUnCom).replace(',', '.')) || 0
	const m = Number(margem) || 0
	const bruto = m === 0 ? compra : compra * (1 + m / 100)
	const duasCasas = Math.round((bruto + Number.EPSILON) * 100) / 100
	return arredondarPreco(duasCasas, arred)
}

/** Aplica uma margem geral a todos os itens (define margemDeLucro e precoFinalVarejo). */
export function aplicarMargemGeral(produtos: ProdutoDaImportacao[], margem: number, arred?: OpcoesArredondamento): void {
	(produtos || []).forEach(p => {
		if (p.isProdutoEstaAgrupado) return
		p.margemDeLucro = margem
		p.precoFinalVarejo = precoComMargem(p.nota.vUnCom, margem, arred)
	})
}

/** Extrai Cor e Tamanho do xProd de uma linha da nota ("... Cor: 001 Tam: M Var: *"). */
export function corTamDoXProd(xProd: string): { cor: string; tam: string } {
	const s = String(xProd || '')
	const cor = (/Cor:\s*([^\s]+)/i.exec(s) || [])[1] || ''
	const tam = (/Tam(?:anho)?:\s*([^\s]+)/i.exec(s) || [])[1] || ''
	return { cor, tam }
}

/**
 * Monta as variantes de um produto pai a partir das LINHAS da própria nota aberta
 * (cada linha irmã = uma variante já existente no XML): Cor/Tam do xProd, EAN do cEAN,
 * quantidade do qCom. Evita a montagem manual de grade.
 */
export function variantesDaNotaAberta(itemReferencia: any, produtoBase: any): any[] {
	const linhas: any[] = (itemReferencia.produtosAgrupados && itemReferencia.produtosAgrupados.length)
		? itemReferencia.produtosAgrupados
		: [itemReferencia]

	return linhas.map((linha: any, index: number) => {
		const { cor, tam } = corTamDoXProd(linha.nota.xProd)
		const atributosVariante: any[] = []
		if (tam) atributosVariante.push({ id: '', nome: 'Tamanho', valor: tam })
		if (cor) atributosVariante.push({ id: '', nome: 'Cor', valor: cor })
		const eanNota = linha.nota.cEAN && linha.nota.cEAN !== 'SEM GTIN' ? linha.nota.cEAN : null
		return {
			id: '', ncm: produtoBase.ncm || null, cest: null, regraFiscal: null,
			idProdutoPai: produtoBase.id, tipo: 'Variante', tags: produtoBase.tags || [],
			descricao: null, categoria: produtoBase.categoria || null, urlImagens: produtoBase.urlImagens || [],
			sku: (produtoBase.sku || '') + semEspacos(tam.toUpperCase()) + semEspacos(cor.toUpperCase()),
			preco: null, pesoLiquido: produtoBase.pesoLiquido, pesoBruto: produtoBase.pesoBruto,
			descontoVarejo: null, marca: produtoBase.marca || null, nome: produtoBase.nome,
			atributosVariante, temGarantia: produtoBase.temGarantia, regrasFiscais: [],
			eans: [eanNota || gerarEanValido(Date.now() + index)],
			unidadeDeMedida: produtoBase.unidadeDeMedida, identificadores: [],
			produtoDesativado: produtoBase.produtoDesativado, produtoIntegrado: produtoBase.produtoIntegrado,
			genero: produtoBase.genero || null, fatorDeConversao: produtoBase.fatorDeConversao,
			unidadeDeConversao: produtoBase.unidadeDeConversao, estacao: produtoBase.estacao || null,
			isValePresente: false, skuPai: produtoBase.skuPai || produtoBase.sku,
			quantidadeImportacao: Number(linha.nota.qCom) || 0,
			fornecedor: produtoBase.fornecedor || null, precoCompra: null, margemLucro: null,
			nomeCompleto: `${produtoBase.nome} ${cor}/${tam}`,
			varianteVinculada: [], vinculada: false, produtoIncompleto: false,
			familiasDoProduto: produtoBase.familiasDoProduto,
			tipoDaOrigemDoProduto: produtoBase.tipoDaOrigemDoProduto,
			codigoOrigemDoProduto: produtoBase.codigoOrigemDoProduto,
		}
	})
}

// --- Totais (resumo) ---

export interface TotaisDaImportacao {
	itens: number
	quantidade: number
	valor: number
}

/** Soma quantidade (qCom) e valor (vProd) dos itens ativos (não agrupados). */
export function totaisDaImportacao(produtos: ProdutoDaImportacao[]): TotaisDaImportacao {
	const ativos = (produtos || []).filter(p => !p.isProdutoEstaAgrupado)
	const numero = (v: string) => Number(String(v == null ? '' : v).replace(',', '.')) || 0
	const quantidade = ativos.reduce((s, p) => s + numero(p.nota.qCom), 0)
	const valor = ativos.reduce((s, p) => s + numero(p.nota.vProd), 0)
	return { itens: ativos.length, quantidade, valor }
}

// --- Serialização para finalizar (enxuga campos volumosos antes de subir ao S3) ---

const CAMPOS_PRODUTO = ['urlImagens', 'familiasDoProduto']
const CAMPOS_VARIANTE = ['urlImagens', 'familiasDoProduto', 'varianteVinculada']

function enxutarProduto(produto: any): any {
	if (!produto) return null
	const slim: any = { ...produto }
	CAMPOS_PRODUTO.forEach(campo => { slim[campo] = undefined })
	if (slim.tipo === 'Com Variantes' && Array.isArray(slim.variantes)) {
		slim.variantes = slim.variantes.map((v: any) => {
			const sv = { ...v }
			CAMPOS_VARIANTE.forEach(campo => { sv[campo] = undefined })
			return sv
		})
	}
	return slim
}

/** Remove campos pesados do produto/variantes mantendo o contrato esperado pelo /finalizar. */
export function enxutarImportacao(importacao: ImportacaoDeNota): ImportacaoDeNota {
	return {
		...importacao,
		// SEGURANÇA: linhas agrupadas (variantes da referência) NÃO vão ao backend —
		// senão o estoque seria movimentado em duplicidade. Vai só o item-referência.
		produtos: importacao.produtos.filter(p => !p.isProdutoEstaAgrupado).map(p => ({
			...p,
			encontrado: enxutarProduto(p.encontrado),
			produtosAgrupados: p.produtosAgrupados
				? p.produtosAgrupados.map(pa => ({ ...pa, encontrado: enxutarProduto(pa.encontrado) }))
				: [],
		})),
	}
}
